/**
 * App — root component.
 *
 * Wires up:
 *  1. AINativeProvider (context + auth + API client)
 *  2. BrowserRouter
 *  3. Auth gate (shows LoginScreen when no token, AppShell+routes when authed)
 *  4. React Router routes from registry
 *
 * Extension point: add screens to src/routes/registry.tsx — App.tsx does NOT
 * need to change when new screens are added.
 */

import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AINativeProvider, useAuth } from "./providers/AINativeProvider.js";
import { AppShell } from "./components/AppShell.js";
import { LoginScreen } from "./components/LoginScreen.js";
import { ROUTES } from "./routes/registry.js";

// ─── Auth gate ────────────────────────────────────────────────────────────────

export function AuthGate({ children }: { children: React.ReactNode }) {
  const { token, loading } = useAuth();

  // While the /me call is in-flight (token set, user not yet loaded),
  // show a minimal loading state rather than flashing the login screen.
  if (loading) {
    return (
      <div
        style={{
          minHeight: "100dvh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--c-bg)",
          color: "var(--c-text-muted)",
          fontFamily: "var(--font-mono)",
          fontSize: "13px",
        }}
        aria-live="polite"
        aria-busy="true"
      >
        loading…
      </div>
    );
  }

  if (!token) {
    return <LoginScreen />;
  }

  return <>{children}</>;
}

// ─── Route tree ───────────────────────────────────────────────────────────────

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<AppShell />}>
        {ROUTES.map((route) => (
          <Route key={route.key} path={route.path} element={route.element} />
        ))}
        {/* Fallback: redirect unknown paths to root */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

// ─── AppInner — provider + gate + routes (no BrowserRouter) ──────────────────
// Exported so tests can wrap with their own router (MemoryRouter).

export function AppInner() {
  return (
    <AINativeProvider>
      <AuthGate>
        <AppRoutes />
      </AuthGate>
    </AINativeProvider>
  );
}

// ─── Root App — adds BrowserRouter for real browser use ──────────────────────

export function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}
